import React from 'react';
import { Modal, Button, Form, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import OrderApi from '../../api/OrderApi';

interface Props {
    show: boolean;
    onHide: () => void;
    orders: any[];
    onDeleted?: () => void;
}

interface State {
    selectedIds: Set<number>;
    isLoading: boolean;
    error: string | null;
    successMsg: string | null;
    confirmMode: 'none' | 'bulk' | 'all';
    confirmInput: string;
}

const initialState: State = {
    selectedIds: new Set(),
    isLoading: false,
    error: null,
    successMsg: null,
    confirmMode: 'none',
    confirmInput: '',
};

class OrderDeleteModal extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = initialState;
    }

    resetAndClose = () => {
        this.setState(initialState);
        this.props.onHide();
    };

    toggleOne = (id: number) => {
        this.setState(prev => {
            const next = new Set(prev.selectedIds);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return { selectedIds: next };
        });
    };

    toggleAll = () => {
        const { orders } = this.props;
        const { selectedIds } = this.state;
        const allSelected = orders.length > 0 && orders.every(o => selectedIds.has(o.id));
        if (allSelected) {
            this.setState({ selectedIds: new Set() });
        } else {
            this.setState({ selectedIds: new Set(orders.map(o => o.id)) });
        }
    };

    askConfirmBulk = () => {
        if (this.state.selectedIds.size === 0) return;
        this.setState({ confirmMode: 'bulk', confirmInput: '', error: null });
    };

    askConfirmAll = () => {
        this.setState({ confirmMode: 'all', confirmInput: '', error: null });
    };

    cancelConfirm = () => {
        this.setState({ confirmMode: 'none', confirmInput: '' });
    };

    doDeleteBulk = async () => {
        const ids = Array.from(this.state.selectedIds);
        this.setState({ isLoading: true, error: null });
        const response = await OrderApi.deleteBulk(ids);
        if (!response.success) {
            this.setState({ isLoading: false, error: response.error || 'Silme başarısız.' });
            return;
        }
        const count = typeof response.data === 'number' ? response.data : ids.length;
        this.setState({
            isLoading: false,
            successMsg: `${count} sipariş silindi.`,
            selectedIds: new Set(),
            confirmMode: 'none',
            confirmInput: '',
        });
        if (this.props.onDeleted) this.props.onDeleted();
    };

    doDeleteAll = async () => {
        this.setState({ isLoading: true, error: null });
        const response = await OrderApi.deleteAll();
        if (!response.success) {
            this.setState({ isLoading: false, error: response.error || 'Silme başarısız.' });
            return;
        }
        const count = typeof response.data === 'number' ? response.data : 0;
        this.setState({
            isLoading: false,
            successMsg: `${count} sipariş silindi (tümü).`,
            selectedIds: new Set(),
            confirmMode: 'none',
            confirmInput: '',
        });
        if (this.props.onDeleted) this.props.onDeleted();
    };

    renderConfirm() {
        const { confirmMode, confirmInput, isLoading, error } = this.state;
        const { selectedIds } = this.state;

        const isBulk = confirmMode === 'bulk';
        const expectedWord = isBulk ? 'SIL' : 'TUM_SIPARISLERI_SIL';
        const canConfirm = confirmInput === expectedWord;

        return (
            <Alert variant="danger">
                <h6 className="mb-2">⚠️ Onay Gerekli</h6>
                <p className="mb-2">
                    {isBulk
                        ? <><strong>{selectedIds.size} sipariş</strong> kalıcı olarak silinecek. Bu işlem geri alınamaz.</>
                        : <><strong>TÜM SİPARİŞLER</strong> kalıcı olarak silinecek. Bu işlem geri alınamaz.</>
                    }
                </p>
                <Form.Group className="mb-2">
                    <Form.Label>
                        Onaylamak için <code>{expectedWord}</code> yazın:
                    </Form.Label>
                    <Form.Control
                        type="text"
                        value={confirmInput}
                        onChange={e => this.setState({ confirmInput: e.target.value })}
                        disabled={isLoading}
                        autoFocus
                    />
                </Form.Group>
                {error && <Alert variant="danger" className="mb-2 py-2">{error}</Alert>}
                <div className="d-flex gap-2">
                    <Button variant="secondary" onClick={this.cancelConfirm} disabled={isLoading}>
                        Vazgeç
                    </Button>
                    <Button
                        variant="danger"
                        onClick={isBulk ? this.doDeleteBulk : this.doDeleteAll}
                        disabled={!canConfirm || isLoading}
                    >
                        {isLoading
                            ? <><Spinner animation="border" size="sm" className="me-2" />Siliniyor...</>
                            : (isBulk ? 'Seçilileri Sil' : 'TÜM Siparişleri Sil')}
                    </Button>
                </div>
            </Alert>
        );
    }

    render() {
        const { show, orders } = this.props;
        const { selectedIds, error, successMsg, confirmMode } = this.state;

        const allSelected = orders.length > 0 && orders.every(o => selectedIds.has(o.id));

        return (
            <Modal show={show} onHide={this.resetAndClose} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Sipariş Sil <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: 11 }}>Geçici araç</Badge></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {successMsg && <Alert variant="success">{successMsg}</Alert>}
                    {error && confirmMode === 'none' && <Alert variant="danger">{error}</Alert>}

                    {confirmMode !== 'none' && this.renderConfirm()}

                    {confirmMode === 'none' && (
                        <>
                            <div className="mb-2 d-flex justify-content-between align-items-center">
                                <strong>Listelenen siparişler ({orders.length})</strong>
                                <Badge bg="info">{selectedIds.size} seçili</Badge>
                            </div>

                            {orders.length === 0 ? (
                                <Alert variant="secondary" className="mb-0">
                                    Listede sipariş yok. Önce filtreleyip sayfada listeleyin.
                                </Alert>
                            ) : (
                                <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                                    <Table size="sm" hover>
                                        <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                                            <tr>
                                                <th style={{ width: 40 }}>
                                                    <Form.Check
                                                        id="delete-toggle-all"
                                                        type="checkbox"
                                                        checked={allSelected}
                                                        onChange={this.toggleAll}
                                                    />
                                                </th>
                                                <th>ID</th>
                                                <th>Cari</th>
                                                <th>Tarih</th>
                                                <th>Durum</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orders.map((o: any) => (
                                                <tr key={o.id}>
                                                    <td>
                                                        <Form.Check
                                                            id={`delete-line-${o.id}`}
                                                            type="checkbox"
                                                            checked={selectedIds.has(o.id)}
                                                            onChange={() => this.toggleOne(o.id)}
                                                        />
                                                    </td>
                                                    <td>{o.id}</td>
                                                    <td>{o.buyerTitle || o.buyer?.title || '-'}</td>
                                                    <td>{o.createdAt ? new Date(o.createdAt).toLocaleDateString('tr-TR') : '-'}</td>
                                                    <td>{o.status || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                {confirmMode === 'none' && (
                    <Modal.Footer className="justify-content-between">
                        <Button variant="secondary" onClick={this.resetAndClose}>Kapat</Button>
                        <div className="d-flex gap-2">
                            <Button
                                variant="danger"
                                disabled={selectedIds.size === 0}
                                onClick={this.askConfirmBulk}
                            >
                                Seçilileri Sil ({selectedIds.size})
                            </Button>
                            <Button variant="outline-danger" onClick={this.askConfirmAll}>
                                Hepsini Sil
                            </Button>
                        </div>
                    </Modal.Footer>
                )}
            </Modal>
        );
    }
}

export default OrderDeleteModal;
